import { type ChangeEvent, type FormEvent, useState } from 'react';
import { api } from '../../../api/client';
import styles from './StudentForm.module.css';

interface StudentData {
  id?: string;
  firstName?: string;
  paternalLastName?: string;
  maternalLastName?: string;
  curp?: string;
  birthDate?: string;
  bloodType?: string;
  allergies?: string;
  emergencyContact?: string;
  fatherFirstName?: string;
  fatherPaternalLastName?: string;
  fatherMaternalLastName?: string;
  fatherEmail?: string;
  motherFirstName?: string;
  motherPaternalLastName?: string;
  motherMaternalLastName?: string;
  motherEmail?: string;
}

interface StudentFormProps {
  onClose: () => void;
  onSuccess: () => void;
  student?: StudentData;
}

export default function StudentForm({ onClose, onSuccess, student }: StudentFormProps) {
  const [formData, setFormData] = useState({
    firstName: student?.firstName || '',
    paternalLastName: student?.paternalLastName || '',
    maternalLastName: student?.maternalLastName || '',
    curp: student?.curp || '',
    birthDate: student?.birthDate ? student.birthDate.split('T')[0] : '',
    bloodType: student?.bloodType || '',
    allergies: student?.allergies || '',
    emergencyContact: student?.emergencyContact || '',
    fatherFirstName: student?.fatherFirstName || '',
    fatherPaternalLastName: student?.fatherPaternalLastName || '',
    fatherMaternalLastName: student?.fatherMaternalLastName || '',
    fatherEmail: student?.fatherEmail || '',
    motherFirstName: student?.motherFirstName || '',
    motherPaternalLastName: student?.motherPaternalLastName || '',
    motherMaternalLastName: student?.motherMaternalLastName || '',
    motherEmail: student?.motherEmail || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const isEditing = !!student?.id;

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMsg('');

    try {
      if (isEditing) {
        await api.put(`/api/students/${student.id}`, formData);
      } else {
        await api.post('/api/students', formData);
      }

      onSuccess();
      setTimeout(() => onClose(), 2000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const title = isEditing ? 'Editar Alumno' : 'Registrar Nuevo Alumno';
  const btnText = isEditing
    ? loading
      ? 'Guardando...'
      : 'Guardar Cambios'
    : loading
      ? 'Guardando...'
      : 'Registrar Alumno';

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>{title}</h2>
          <button onClick={onClose} className={styles.closeBtn}>
            &times;
          </button>
        </div>

        {error && <div className={styles.error}>{error}</div>}
        {successMsg && <div className={styles.success}>{successMsg}</div>}

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.section}>
            <h3>Datos del Alumno</h3>
            <div className={styles.grid}>
              <div className={styles.field}>
                <label>Nombre(s)</label>
                <input
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  placeholder="Ej: Juan"
                />
              </div>
              <div className={styles.field}>
                <label>Apellido Paterno</label>
                <input
                  name="paternalLastName"
                  value={formData.paternalLastName}
                  onChange={handleChange}
                  required
                  placeholder="Ej: Pérez"
                />
              </div>
              <div className={styles.field}>
                <label>Apellido Materno</label>
                <input
                  name="maternalLastName"
                  value={formData.maternalLastName}
                  onChange={handleChange}
                  placeholder="Ej: García"
                />
              </div>
              <div className={styles.field}>
                <label>CURP (18 caracteres)</label>
                <input
                  name="curp"
                  value={formData.curp}
                  onChange={handleChange}
                  maxLength={18}
                  placeholder="ABCD123456HDFR..."
                />
              </div>
              <div className={styles.field}>
                <label>Fecha de Nacimiento</label>
                <input
                  name="birthDate"
                  type="date"
                  value={formData.birthDate}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <h3>Padre / Tutor</h3>
            <p className={styles.sectionNote}>
              Se generará automáticamente un acceso a la aplicación de padres
            </p>
            <div className={styles.grid}>
              <div className={styles.field}>
                <label>Nombre</label>
                <input
                  name="fatherFirstName"
                  value={formData.fatherFirstName}
                  onChange={handleChange}
                  placeholder="Ej: Carlos"
                />
              </div>
              <div className={styles.field}>
                <label>Apellido Paterno</label>
                <input
                  name="fatherPaternalLastName"
                  value={formData.fatherPaternalLastName}
                  onChange={handleChange}
                  placeholder="Ej: Pérez"
                />
              </div>
              <div className={styles.field}>
                <label>Apellido Materno</label>
                <input
                  name="fatherMaternalLastName"
                  value={formData.fatherMaternalLastName}
                  onChange={handleChange}
                  placeholder="Ej: López"
                />
              </div>
              <div className={styles.field}>
                <label>Correo electrónico</label>
                <input
                  name="fatherEmail"
                  type="email"
                  value={formData.fatherEmail}
                  onChange={handleChange}
                  placeholder="carlos@ejemplo.com"
                />
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <h3>Madre / Tutora</h3>
            <div className={styles.grid}>
              <div className={styles.field}>
                <label>Nombre</label>
                <input
                  name="motherFirstName"
                  value={formData.motherFirstName}
                  onChange={handleChange}
                  placeholder="Ej: María"
                />
              </div>
              <div className={styles.field}>
                <label>Apellido Paterno</label>
                <input
                  name="motherPaternalLastName"
                  value={formData.motherPaternalLastName}
                  onChange={handleChange}
                  placeholder="Ej: López"
                />
              </div>
              <div className={styles.field}>
                <label>Apellido Materno</label>
                <input
                  name="motherMaternalLastName"
                  value={formData.motherMaternalLastName}
                  onChange={handleChange}
                  placeholder="Ej: García"
                />
              </div>
              <div className={styles.field}>
                <label>Correo electrónico</label>
                <input
                  name="motherEmail"
                  type="email"
                  value={formData.motherEmail}
                  onChange={handleChange}
                  placeholder="maria@ejemplo.com"
                />
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <h3>Información Médica y Seguridad</h3>
            <div className={styles.grid}>
              <div className={styles.field}>
                <label>Tipo de Sangre</label>
                <select name="bloodType" value={formData.bloodType} onChange={handleChange}>
                  <option value="">Seleccionar...</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="AB+">AB+</option>
                </select>
              </div>
              <div className={styles.field}>
                <label>Contacto de Emergencia</label>
                <input
                  name="emergencyContact"
                  value={formData.emergencyContact}
                  onChange={handleChange}
                  placeholder="Nombre y Teléfono"
                />
              </div>
              <div className={styles.fieldFull}>
                <label>Alergias / Observaciones Médicas</label>
                <textarea
                  name="allergies"
                  value={formData.allergies}
                  onChange={handleChange}
                  placeholder="Indique si padece alguna alergia o condición especial"
                />
              </div>
            </div>
          </div>

          <div className={styles.footer}>
            <button type="button" onClick={onClose} className={styles.cancelBtn}>
              Cancelar
            </button>
            <button type="submit" disabled={loading} className={styles.submitBtn}>
              {btnText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
